import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getMovieById, getRelatedMovies } from "@/mocks/movies";
import CommentSection from "./components/CommentSection";

const metaLabels = (movie: NonNullable<ReturnType<typeof getMovieById>>) => [
  { label: "导演", value: movie.director, icon: "ri-clapperboard-line" },
  { label: "主演", value: movie.cast.join(" / "), icon: "ri-user-star-line" },
  { label: "上映年份", value: `${movie.year} 年`, icon: "ri-calendar-line" },
  { label: "影片类型", value: movie.genres.join(" / "), icon: "ri-price-tag-3-line" },
  { label: "片长", value: `${movie.duration} 分钟`, icon: "ri-time-line" },
  { label: "制片地区", value: movie.region, icon: "ri-earth-line" },
];

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);

  usePageMeta(
    movie
      ? `${movie.title} - 详细剧情解析与推荐 - 韩国三级电影`
      : "影片未找到 - 韩国三级电影",
    movie
      ? `${movie.title}（${movie.year}）由${movie.director}执导，${movie.cast.join("、")}主演。本站整理影片剧情简介、主创信息、结构解析与观众评论摘要。`
      : "该影片资料不存在或已被移除，请返回首页浏览其他韩国影视作品。",
    movie
      ? `${movie.title},${movie.genres.join(",")},韩国三级电影,${movie.director},影片解析`
      : "韩国三级电影,影片资料",
  );

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1360px] px-4 py-16 md:px-8 md:py-24">
          <EmptyState
            icon="ri-file-unknow-line"
            title="影片未找到"
            desc="你访问的影片资料不存在，可能链接有误或该条目已被移除。可以回到首页继续浏览其他作品。"
          />
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const stars = Math.round(movie.rating / 2);

  return (
    <SiteLayout>
      <div className="relative w-full overflow-hidden border-b border-background-200/60">
        <div className="absolute inset-0">
          <img
            src={movie.poster}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-110 object-cover object-top opacity-30 blur-2xl"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/90 to-background-50"></div>

        <div className="relative mx-auto w-full max-w-[1360px] px-4 pb-10 pt-6 md:px-8 md:pb-14 md:pt-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影片分类", to: "/category" },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
            <div className="md:col-span-4 lg:col-span-3">
              <div className="overflow-hidden rounded-lg border border-background-300/60">
                <div className="aspect-[2/3] w-full bg-background-200">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 高清影片海报`}
                    title={`${movie.title} 影片资料与解析`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between rounded-lg border border-background-200/80 bg-background-100/80 px-4 py-3">
                <div>
                  <div className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-500">
                    Rating
                  </div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-display text-3xl font-semibold leading-none text-accent-500">
                      {movie.rating.toFixed(1)}
                    </span>
                    <span className="text-xs text-foreground-500">/ 10</span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <i
                      key={index}
                      className={
                        index < stars
                          ? "ri-star-fill text-sm text-accent-500"
                          : "ri-star-line text-sm text-foreground-500"
                      }
                    ></i>
                  ))}
                </div>
              </div>
            </div>

            <div className="md:col-span-8 lg:col-span-9">
              <div className="flex flex-wrap items-center gap-2">
                {movie.collection.includes("new") && (
                  <span className="rounded bg-accent-500 px-2 py-1 font-label text-[10px] font-semibold uppercase tracking-wider text-background-50">
                    New Release
                  </span>
                )}
                {movie.collection.includes("hot") && (
                  <span className="rounded bg-primary-500 px-2 py-1 font-label text-[10px] font-semibold uppercase tracking-wider text-background-50">
                    Trending
                  </span>
                )}
                <span className="rounded border border-background-300 px-2 py-1 text-[10px] text-foreground-600">
                  {movie.region}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-foreground-500">
                  <i className="ri-eye-line"></i>
                  {(movie.views / 1000).toFixed(1)}k 次浏览
                </span>
              </div>

              <h1 className="mt-3 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-[42px]">
                {movie.title}
              </h1>
              <p className="mt-1 font-label text-sm uppercase tracking-[0.22em] text-foreground-500">
                {movie.originalTitle} · {movie.year}
              </p>

              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 border-y border-background-200/70 py-4 sm:grid-cols-2 lg:grid-cols-3">
                {metaLabels(movie).map((item) => (
                  <div key={item.label} className="flex items-start gap-2">
                    <i className={`${item.icon} mt-0.5 text-sm text-primary-400`}></i>
                    <div className="min-w-0">
                      <div className="text-[11px] text-foreground-500">{item.label}</div>
                      <div className="mt-0.5 text-sm text-foreground-800">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">剧情简介</h2>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{movie.synopsis}</p>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-background-300/70 bg-background-100/60 px-2.5 py-1 text-[11px] text-foreground-600"
                  >
                    # {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="#related"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-movie-line"></i>
                  查看相关推荐
                </a>
                <Link
                  to={`/category?genre=${encodeURIComponent(movie.genres[0])}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  <i className="ri-links-line"></i>
                  同类影片
                </Link>
                <span className="text-[11px] text-foreground-500">本站不提供播放与下载入口</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-10 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <article className="rounded-lg border border-background-200/80 bg-background-100/40 p-5 md:p-7">
              <div className="flex items-center gap-2">
                <span className="h-3 w-[3px] rounded-full bg-accent-500"></span>
                <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
                  Analysis
                </span>
              </div>
              <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                剧情解析与观影笔记
              </h2>

              <div className="mt-5 space-y-4">
                {movie.analysis.map((paragraph, index) => (
                  <div key={index} className="flex gap-3">
                    <span className="mt-0.5 shrink-0 font-display text-sm font-semibold text-background-400">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="text-sm leading-8 text-foreground-600">{paragraph}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-md border-l-2 border-primary-500 bg-background-50 px-4 py-3">
                <p className="text-xs leading-6 text-foreground-500">
                  以上解析为编辑基于公开资料整理的观点，含剧情描述，建议观看后再阅读。
                </p>
              </div>
            </article>

            <div className="mt-6">
              <CommentSection comments={movie.comments} />
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="space-y-4 lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200/80 bg-background-100/50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">影片资料卡</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <dt className="shrink-0 text-xs text-foreground-500">中文片名</dt>
                    <dd className="text-right text-foreground-800">{movie.title}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="shrink-0 text-xs text-foreground-500">原文片名</dt>
                    <dd className="text-right font-label text-xs uppercase tracking-wider text-foreground-800">
                      {movie.originalTitle}
                    </dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="shrink-0 text-xs text-foreground-500">导演</dt>
                    <dd className="text-right text-foreground-800">{movie.director}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="shrink-0 text-xs text-foreground-500">主演</dt>
                    <dd className="text-right text-foreground-800">{movie.cast.join(" / ")}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="shrink-0 text-xs text-foreground-500">年份 / 片长</dt>
                    <dd className="text-right text-foreground-800">
                      {movie.year} · {movie.duration} 分钟
                    </dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="shrink-0 text-xs text-foreground-500">类型</dt>
                    <dd className="text-right text-foreground-800">{movie.genres.join(" / ")}</dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-background-200/70 pt-4">
                  <div className="mb-2 flex items-center justify-between text-xs text-foreground-500">
                    <span>站内评分</span>
                    <span className="font-label text-sm font-semibold text-accent-500">
                      {movie.rating.toFixed(1)}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500"
                      style={{ width: `${movie.rating * 10}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200/80 bg-background-100/50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">继续浏览</h2>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Link
                    to="/category?c=new"
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    最新上映
                  </Link>
                  <Link
                    to="/category?c=classic"
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    经典收藏
                  </Link>
                  <Link
                    to="/category?c=hot"
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    热门推荐
                  </Link>
                  <Link
                    to="/search"
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    搜索影片
                  </Link>
                </div>
              </div>

              <div className="rounded-lg border border-primary-800/60 bg-primary-950/50 p-5">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-base text-accent-400"></i>
                  <h3 className="text-sm font-semibold text-primary-50">版权与免责说明</h3>
                </div>
                <p className="mt-2 text-xs leading-6 text-primary-200/70">
                  本页海报与影片资料均为演示用途整理，不含播放、下载链接。内容面向成年观众，请自行判断是否适合浏览。
                </p>
              </div>
            </div>
          </aside>
        </div>

        <section id="related" className="mt-12 scroll-mt-24">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-[3px] rounded-full bg-accent-500"></span>
                <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
                  You may also like
                </span>
              </div>
              <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关推荐
              </h2>
            </div>
            <Link
              to="/category"
              className="group inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-accent-400"
            >
              全部影片
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {related.map((item, index) => (
              <MovieCard key={item.id} movie={item} index={index} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}